import React from 'react';
import { getIn, useFormikContext } from 'formik';

import { FORMIK_FINANCE_FIELD_IDS } from '~/components/Contacts/UpsertContact/Tabs/Finance/constants';
import { getFullFinancePath } from '~/components/Contacts/UpsertContact/Tabs/Finance/utils';
import UploadDocument from '~/components/Contacts/UpsertContact/UploadDocument';
import { DOCUMENT_FIELD_IDS } from '~/components/Contacts/UpsertContact/UploadDocument/constants';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Link from '~/components/core/Atomic/Link/Link';
import Caption from '~/components/core/TextComponents/Caption';
import { useCms } from '~/components/hooks/useCms';
import CloseIcon from '~/components/icons/CloseIcon';

interface BankProofDocumentProps {
  disabled?: boolean;
  showOnly?: boolean;
  documentUploadPercent?: number;
}

const BankProofDocument: React.FC<BankProofDocumentProps> = ({ disabled, showOnly, documentUploadPercent = 0 }) => {
  const { originalContact, isCreation } = useContact();
  const { userOrganization } = useCms();
  const { values, setFieldValue } = useFormikContext();

  const hasExistingDocument = Boolean(originalContact?.bank_proof_contact_document_id);
  const hasFileAssigned = Boolean(getIn(values, getFullFinancePath(DOCUMENT_FIELD_IDS.FILE)));
  const isMarkedForRemoval = Boolean(
    getIn(values, getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.REMOVE_BANK_PROOF_DOCUMENT))
  );

  const handleRemoveDocument = () => {
    setFieldValue(getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.REMOVE_BANK_PROOF_DOCUMENT), true);
  };

  // Don't show upload during contact creation (upload requires existing contact ID)
  if (isCreation) {
    return null;
  }

  // Don't show anything if disabled and no existing/assigned document
  if (disabled && !hasExistingDocument && !hasFileAssigned) {
    return null;
  }

  // Show existing document with option to remove
  if (hasExistingDocument && !isMarkedForRemoval && !hasFileAssigned) {
    return (
      <div className="col-span-full">
        <Caption variant={Caption.VARIANTS.LABEL}>Bank Proof Document</Caption>
        <div className="mt-8 flex items-center gap-8">
          <Link
            className="font-medium text-teal-700 underline"
            href={`/api/v1/organizations/${userOrganization.id}/contacts/${originalContact?.id}/bank_proof`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Document
          </Link>
          {!disabled && !showOnly && (
            <IconButton size="small" onClick={handleRemoveDocument} aria-label="Remove document">
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <UploadDocument
      disabled={disabled}
      showOnly={showOnly}
      documentFieldKey={FORMIK_FINANCE_FIELD_IDS.BANK_PROOF_DOCUMENT_ID}
      documentType="bank_proof"
      documentFieldLabel="Bank Proof Document"
      getFullPath={getFullFinancePath}
      documentUploadPercent={documentUploadPercent}
    />
  );
};

export default BankProofDocument;
